import { useCallback, useEffect, useState } from "react";
import { mcpCall, consumerIdOrThrow } from "../lib/mcp-client";

// Twin profile cell-by-cell editor per LS-SPEC-01. Read prefs via
// twin.preferences, write via twin.write_cell, delete via
// twin.delete_cell (cascades per LS-SPEC-07 §Cryptographic Erasure).
//
// POC scope: tabular per-category view + add-cell / edit-value /
// delete-cell rows. Production wires a richer applies_when context
// editor + provenance drilldown.

interface PreferenceCell {
  value: unknown;
  source?: string;
  written_at?: number;
  applies_when?: Record<string, unknown>;
  confidence?: number;
}

type Preferences = Record<string, Record<string, PreferenceCell>>;


export function TwinProfileEditor() {
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<string>("food");

  const load = useCallback(async () => {
    setErr(null);
    try {
      const id = consumerIdOrThrow();
      const r = await mcpCall<{ preferences: Preferences }>(
        "twin.preferences", { consumer_id: id },
      );
      setPrefs(r.preferences || {});
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const write = async (category: string, key: string, value: string) => {
    try {
      await mcpCall("twin.write_cell", {
        consumer_id: consumerIdOrThrow(),
        category, key, value,
        source: "consumer_explicit",
      });
      load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const remove = async (category: string, key: string) => {
    if (!window.confirm(`Forget ${category}.${key}?`)) return;
    try {
      await mcpCall("twin.delete_cell", {
        consumer_id: consumerIdOrThrow(),
        category, key,
      });
      load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const categories = Object.keys(prefs || {});
  const cells = Object.entries((prefs && prefs[active]) || {});

  return (
    <div className="flex h-full flex-col gap-3 px-4 py-3">
      <header>
        <h1 className="text-base font-medium text-(--color-ink)">
          Twin profile
        </h1>
        <p className="text-xs text-(--color-ink-muted)">
          Every preference cell, with provenance. Edit or remove any
          at any time. Deletion cascades through derived beliefs.
        </p>
      </header>

      {err && (
        <p className="rounded-md bg-red-900/40 px-2 py-1 text-xs text-red-200">
          {err}
        </p>
      )}

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1.5 text-xs">
        {(categories.length ? categories : ["food", "wellness",
            "travel.hotel", "fashion"]).map((c) => (
          <button
            key={c}
            onClick={() => setActive(c)}
            className={[
              "rounded-(--radius-pill) px-2.5 py-1",
              active === c
                ? "bg-(--color-accent-gold) text-stone-900"
                : "bg-(--color-canvas-card) text-(--color-ink-muted) hover:text-(--color-ink)",
            ].join(" ")}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Cell table for active category */}
      <section className="rounded-xl border border-white/5 bg-(--color-canvas-card) p-3">
        {cells.length === 0 ? (
          <p className="text-xs text-(--color-ink-faint)">
            No preferences in <span className="font-mono">{active}</span> yet.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {cells.map(([key, cell]) => (
              <CellRow
                key={key}
                category={active}
                cellKey={key}
                cell={cell}
                onWrite={write}
                onRemove={remove}
              />
            ))}
          </ul>
        )}
        <AddCellRow category={active} onAdd={write} />
      </section>
    </div>
  );
}


function CellRow({
  category, cellKey, cell, onWrite, onRemove,
}: {
  category: string;
  cellKey: string;
  cell: PreferenceCell;
  onWrite: (cat: string, key: string, value: string) => void;
  onRemove: (cat: string, key: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    cell.value === null || cell.value === undefined
      ? "" : String(cell.value),
  );

  const save = () => {
    onWrite(category, cellKey, draft);
    setEditing(false);
  };

  return (
    <li className="flex items-baseline gap-2 text-xs">
      <span className="w-32 truncate text-(--color-ink-muted)">{cellKey}</span>
      {editing ? (
        <>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="flex-1 rounded-md bg-(--color-canvas-elev) px-2 py-0.5 text-(--color-ink)"
            aria-label={`Edit ${cellKey}`}
          />
          <button onClick={save}
                  className="rounded-(--radius-pill) bg-(--color-accent-gold) px-2 py-0.5 text-stone-900">
            Save
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 truncate font-mono text-(--color-ink)">
            {String(cell.value)}
          </span>
          <span className="text-(--color-ink-faint) text-[10px] w-20 truncate">
            {cell.source ?? ""}
          </span>
          <button onClick={() => setEditing(true)}
                  className="rounded-(--radius-pill) px-2 py-0.5 text-(--color-ink-faint) hover:text-(--color-ink)">
            Edit
          </button>
          <button onClick={() => onRemove(category, cellKey)}
                  className="rounded-(--radius-pill) px-2 py-0.5 text-(--color-ink-faint) hover:text-red-300">
            Forget
          </button>
        </>
      )}
    </li>
  );
}


function AddCellRow({
  category, onAdd,
}: {
  category: string;
  onAdd: (cat: string, key: string, value: string) => void;
}) {
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");

  const submit = () => {
    if (!key.trim() || !value.trim()) return;
    onAdd(category, key.trim(), value.trim());
    setKey(""); setValue("");
  };

  return (
    <div className="mt-3 flex items-baseline gap-2 text-xs border-t border-white/5 pt-2">
      <span className="w-32 text-(--color-ink-faint)">+ new cell</span>
      <input
        value={key} onChange={(e) => setKey(e.target.value)}
        placeholder="key"
        className="w-32 rounded-md bg-(--color-canvas-elev) px-2 py-0.5 text-(--color-ink)"
        aria-label="New cell key"
      />
      <input
        value={value} onChange={(e) => setValue(e.target.value)}
        placeholder="value"
        className="flex-1 rounded-md bg-(--color-canvas-elev) px-2 py-0.5 text-(--color-ink)"
        aria-label="New cell value"
      />
      <button onClick={submit}
              disabled={!key.trim() || !value.trim()}
              className="rounded-(--radius-pill) bg-(--color-accent-gold) px-2 py-0.5 text-stone-900 disabled:opacity-40">
        Add
      </button>
    </div>
  );
}
